import { useEffect } from "react";
import { useQueries, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { PositionsResponse } from "../api/types";
import { setPositionsRefresher } from "../store/dispatch";

/**
 * Позиции оператора по рынку — снимок с сервера.
 *
 * РАЗМЕР в нём с 2026-08-11 приходит из цепочки (баланс ERC-1155), а от чужого
 * индексатора остаются средняя цена, текущая цена и признак погашения; чем
 * подтверждён размер, говорит поле `source` каждой позиции. Причина в
 * CHANGELOG: индексатор молчал четыре минуты про позицию, лежавшую на кошельке,
 * и продать её всё это время было нечем.
 *
 * Опрос REST, а не сокет: потока по позициям нет вовсе. Оба zustand-стора данных
 * (useBooks, useOrders) существуют потому, что СЛИВАЮТ сокетный поток с
 * REST-гидрацией; здесь сливать нечего, и это ровно та форма, для которой в проекте
 * уже применяется TanStack Query.
 *
 * ЧТО ЗДЕСЬ НЕ ЖИВЁТ, начиная с разбора замечаний оператора: сама позиция, которую
 * он читает на экране. Индексатор отстаёт от исполнения на секунды, и обойти это
 * никаким интервалом нельзя. Мгновенное число считается из НАШИХ филлов
 * (lib/fills.ts + store/useFills.ts), а этот снимок остаётся ВТОРЫМ МНЕНИЕМ: он
 * знает про share, набранные не через нас, и он же — основание для размера
 * ордера на продажу (lib/position.ts).
 */
export function positionsKey(conditionID: string | undefined) {
  return ["positions", conditionID ?? ""] as const;
}

/**
 * Ключ с аккаунтом. Префикс — positionsKey: перечитывание после филла
 * инвалидирует рынок целиком, по всем аккаунтам, и не обязано знать, чей был
 * филл.
 */
function accountPositionsKey(conditionID: string | undefined, account: string | null) {
  return [...positionsKey(conditionID), account ?? ""] as const;
}

/** Как часто спрашиваем позиции. */
const POLL_MS = 3000;

/** Снимки аккаунтов: по запросу на аккаунт, в порядке `accounts`. */
export interface AccountsPositions {
  accounts: readonly string[];
  /** Ответ на каждый аккаунт, в том же порядке. */
  queries: UseQueryResult<PositionsResponse>[];
}

/**
 * Позиции НАЗВАННЫХ аккаунтов: у каждого свой кошелёк, и продавать можно
 * только то, что лежит на том, от чьего имени уйдёт ордер.
 *
 * Панель быстрых действий показывает все аккаунты набора сразу (строки по
 * когортам), поэтому спрашивает всех: три аккаунта — три запроса за такт, и
 * бюджет Data API по IP (150 за 10 с) это выдерживает с запасом.
 *
 * Ключ каждого — accountPositionsKey: перечитывание после филла (positionsKey)
 * накрывает всех разом, не зная, чей был филл.
 */
export function usePositionsOf(
  conditionID: string | undefined,
  accounts: readonly string[],
): AccountsPositions {
  const queries = useQueries({
    queries: accounts.map((account) => ({
      queryKey: accountPositionsKey(conditionID, account),
      queryFn: () => api.positions(conditionID ?? "", account),
      enabled: Boolean(conditionID),
      refetchInterval: POLL_MS,
      // Больше серверного TTL (2 с): опрос чаще кеша возвращал бы тот же снимок и
      // тратил бы круг ни на что.
      //
      // В ФОНОВОЙ вкладке опрос НЕ идёт — сознательное расхождение с /status. Там
      // фоновый опрос нужен ради предупреждения об аварийном стопе, которое
      // оператор обязан увидеть, даже глядя в другое окно. Здесь наоборот: в
      // фоновой вкладке никто не жмёт «продать», а за эндпоинтом стоит чужой
      // индексатор со своими лимитами.
      refetchIntervalInBackground: false,
      // Позиция устаревает сама по себе, и держать её «свежей» между
      // перерисовками бессмысленно: спрашиваем по таймеру.
      staleTime: 0,
    })),
  });
  return { accounts, queries };
}

/**
 * Просит немедленно перечитать позиции.
 *
 * Нужен после НАШЕГО исполнения: индексатор отстаёт на секунды, и момент, когда
 * смотреть стоит, известен точно — сразу после сделки. Ждать очередного тика,
 * глядя на размер, который только что изменился, оператору предлагать нельзя.
 */
export function useRefreshPositions(conditionID: string | undefined): () => void {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: positionsKey(conditionID) });
}

/**
 * Подписывает перечитывание позиций на события ИСПОЛНЕНИЯ из сокета.
 *
 * Ставится один раз на приложение, а не на страницу рынка: филл может приехать по
 * рынку, который сейчас не открыт (оператор ушёл на следующее окно серии, а ордер
 * прошлого ещё стоял), и подписка, живущая вместе со страницей, пропустила бы ровно
 * этот случай. Инвалидация по чужому ключу дешёвая: неактивный запрос TanStack
 * Query не перезапрашивает.
 *
 * До этого единственная инвалидация висела на ПРИЁМЕ команды — то есть на
 * постановке ордера. Для тейкера это почти совпадало с исполнением, а для мейкера
 * не значило ничего: его ордер в этот момент только встал в стакан, а на сам филл
 * не срабатывало НИЧЕГО, и позиция ждала очередного тика опроса.
 */
export function usePositionsRefreshOnFill(): void {
  const client = useQueryClient();
  useEffect(() => {
    setPositionsRefresher((conditionID) => {
      void client.invalidateQueries({ queryKey: positionsKey(conditionID) });
    });
    return () => setPositionsRefresher(null);
  }, [client]);
}
